<template>
	<article class="notification-card" :class="{ read: notification.read_at }">
		<div :class="['infos', { read: notification.read_at }]">
			<img :src="notification.image" alt="" @click="
				toNotificationUrl(notification.url, notification.id)
				" />
			<div class="description" @click="
				toNotificationUrl(notification.url, notification.id)
				">
				<div class="description-top">
					<div class="title">
						{{ truncatedText(notification.title, 80) }}
					</div>
					<p v-html="truncatedText(notification.message, 95)"></p>
				</div>
				<div class="description-down">
					{{ notification.created_at }}
				</div>
			</div>

			<div class="action-bar">
				<!-- <div v-if="!notification.read_at" class="unread-circle mx-auto"></div> -->
				<div ref="selectRef">
					<div class="more" @click.prevent.stop="showNotificationModal = !showNotificationModal">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M11 15V17H13V15H11Z" fill="var(--active)" />
							<path d="M11 11V13H13V11H11Z" fill="var(--active)" />
							<path d="M11 7V9H13V7H11Z" fill="var(--active)" />
						</svg>
					</div>
					<MoreNotificationsComponent v-if="showNotificationModal" :id="notification.id"
						@close="showNotificationModal = false" />
				</div>
			</div>
		</div>
	</article>
</template>

<script setup lang="ts">
import { defineProps, ref } from "vue";
import MoreNotificationsComponent from "~/components/ui/Header/MoreNotificationsComponent.vue";
import type { Notification } from "~/composables/useUserNotifications";
import { useNotificationsStore } from "~/stores/UserNotifications";

const locale = useNuxtApp().$i18n.locale;

defineProps<{
	notification: Notification;
}>();

const emit = defineEmits(["close"]);
const showNotificationModal = ref(false);
const selectRef = ref<HTMLElement | null>(null);

const readNotification = async (id: string) => {
	const userNotificationsStore = useNotificationsStore();
	await userNotificationsStore.markNotificationAsRead(id as string);
};

const toNotificationUrl = async (
	url: string | null,
	id: number | null | string,
) => {
	const router = useRouter();
	if (!id) return;

	await readNotification(id as string);
	emit("close");

	if (url && !showNotificationModal.value) {
		if (url.startsWith(window.location.origin)) {
			const relativePath = url.replace(window.location.origin, "");
			router.push(relativePath);
		} else {
			window.open(url, "_blank");
		}
	}
};

const truncatedText = (text: string, size: number) => {
	if (!text) return "";
	if (text.length > size) {
		return text.substring(0, size) + "...";
	}
	return text;
};

const handleClickOutside = (event: MouseEvent) => {
	const target = event.target as Node;
	if (selectRef.value && !selectRef.value.contains(target)) {
		showNotificationModal.value = false;
	}
};

onMounted(() => {
	document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener("click", handleClickOutside);
});
</script>

<style lang="scss" scoped>
.notification-card {
	display: flex;
	height: 136px;
	padding: 16px 8px;
	align-items: center;
	gap: 8px;
	align-self: stretch;
	border-bottom: 1px solid var(--body-bg);
	cursor: pointer;
	background-color: var(--card-article-bg);

	&.read {
		background-color: var(--body-bg);
	}

	.infos.read {
		display: flex;
		height: 104px;
		gap: 8px;
		flex: 1 0 0;

		img {
			width: 104px;
			height: 104px;
			object-fit: cover;
			filter: grayscale(1);
		}

		.description {
			font-size: 14px;
			font-weight: 400;
			line-height: 20px;
			width: 100%;
			max-width: 233px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;

			.description-top {
				.title {
					font-size: 14px;
					font-style: normal;
					font-weight: 400;
					line-height: 20px;
					color: var(--secondary-info-fg);
				}

				p {
					font-size: 12px;
					font-weight: 400;
					line-height: 16px;
					margin-bottom: 5px;
					color: var(--secondary-info-fg);
					overflow-wrap: break-word;
				}
			}

			.description-down {
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
				color: var(--secondary-info-fg);
			}
		}

		.action-bar {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 8px;
			align-self: stretch;

			.more {
				cursor: pointer;
			}

			.unread-circle {
				width: 8px;
				height: 8px;
				background-color: var(--highlight-color);
				border-radius: 100%;
			}
		}
	}

	.infos {
		display: flex;
		height: 104px;
		gap: 8px;
		flex: 1 0 0;

		img {
			width: 100%;
			height: 104px;
			object-fit: cover;
			max-width: 104px;
		}

		.description {
			font-size: 14px;
			font-weight: 400;
			line-height: 20px;
			width: 100%;
			display: flex;
			flex-direction: column;
			justify-content: space-between;

			.description-top {
				.title {
					font-size: 14px;
					font-style: normal;
					font-weight: 500;
					line-height: 20px;
					color: var(--active);
				}

				p {
					font-size: 12px;
					font-weight: 500;
					line-height: 16px;
					color: var(--secondary-info-fg);
				}
			}

			.description-down {
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
				color: var(--secondary-info-fg);
			}
		}

		.action-bar {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 8px;
			align-self: stretch;

			.more {
				cursor: pointer;
			}

			.unread-circle {
				width: 8px;
				height: 8px;
				background-color: var(--highlight-color);
				border-radius: 100%;
			}
		}
	}
}
</style>
